import { useMemo, useState } from 'react';
import type { Prefecture } from '../../../mocks/japanPrefectures';

interface JapanMapProps {
  prefectures: Prefecture[];
  hoveredPref: string | null;
  onHover: (id: string | null) => void;
  onSelect: (id: string) => void;
}

const PREF_POSITIONS: Record<string, { x: number; y: number; w: number; h: number }> = {
  hokkaido:  { x: 310, y: 28, w: 72, h: 62 },
  aomori:    { x: 305, y: 108, w: 42, h: 32 },
  iwate:     { x: 320, y: 142, w: 42, h: 36 },
  miyagi:    { x: 310, y: 180, w: 38, h: 30 },
  akita:     { x: 278, y: 138, w: 36, h: 34 },
  yamagata:  { x: 282, y: 174, w: 32, h: 32 },
  fukushima: { x: 290, y: 208, w: 44, h: 32 },
  ibaraki:   { x: 302, y: 242, w: 34, h: 34 },
  tochigi:   { x: 278, y: 238, w: 30, h: 30 },
  gunma:     { x: 252, y: 232, w: 30, h: 30 },
  saitama:   { x: 268, y: 264, w: 30, h: 24 },
  chiba:     { x: 302, y: 278, w: 34, h: 36 },
  tokyo:     { x: 278, y: 290, w: 26, h: 22 },
  kanagawa:  { x: 272, y: 310, w: 30, h: 22 },
  niigata:   { x: 248, y: 168, w: 40, h: 48 },
  toyama:    { x: 222, y: 202, w: 30, h: 26 },
  ishikawa:  { x: 202, y: 182, w: 26, h: 38 },
  fukui:     { x: 196, y: 222, w: 28, h: 30 },
  yamanashi: { x: 248, y: 268, w: 26, h: 26 },
  nagano:    { x: 232, y: 232, w: 32, h: 40 },
  gifu:      { x: 210, y: 248, w: 32, h: 32 },
  shizuoka:  { x: 238, y: 298, w: 40, h: 28 },
  aichi:     { x: 216, y: 286, w: 32, h: 30 },
  mie:       { x: 196, y: 302, w: 28, h: 36 },
  shiga:     { x: 192, y: 262, w: 22, h: 28 },
  kyoto:     { x: 172, y: 248, w: 28, h: 30 },
  osaka:     { x: 176, y: 286, w: 24, h: 24 },
  hyogo:     { x: 150, y: 268, w: 32, h: 32 },
  nara:      { x: 182, y: 308, w: 20, h: 28 },
  wakayama:  { x: 166, y: 328, w: 30, h: 28 },
  tottori:   { x: 142, y: 242, w: 30, h: 22 },
  shimane:   { x: 112, y: 238, w: 36, h: 24 },
  okayama:   { x: 138, y: 266, w: 28, h: 24 },
  hiroshima: { x: 108, y: 264, w: 34, h: 26 },
  yamaguchi: { x: 82, y: 272, w: 30, h: 26 },
  tokushima: { x: 152, y: 310, w: 26, h: 22 },
  kagawa:    { x: 148, y: 292, w: 24, h: 18 },
  ehime:     { x: 118, y: 300, w: 32, h: 26 },
  kochi:     { x: 122, y: 326, w: 38, h: 24 },
  fukuoka:   { x: 62, y: 278, w: 28, h: 24 },
  saga:      { x: 42, y: 286, w: 24, h: 20 },
  nagasaki:  { x: 22, y: 292, w: 26, h: 28 },
  kumamoto:  { x: 52, y: 308, w: 28, h: 28 },
  oita:      { x: 78, y: 298, w: 28, h: 24 },
  miyazaki:  { x: 72, y: 326, w: 26, h: 30 },
  kagoshima: { x: 46, y: 342, w: 32, h: 34 },
  okinawa:   { x: 18, y: 398, w: 42, h: 28 },
};

const REGION_COLORS: Record<string, string> = {
  'hokkaido-tohoku': '#00d9ff',
  'kanto': '#ff006e',
  'chubu': '#b537f2',
  'kinki': '#00ff41',
  'chugoku-shikoku': '#ff8c00',
  'kyushu': '#ffd600',
};

export function JapanMap({ prefectures, hoveredPref, onHover, onSelect }: JapanMapProps) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; pref: Prefecture } | null>(null);

  const prefMap = useMemo(() => {
    const map: Record<string, Prefecture> = {};
    prefectures.forEach(p => { map[p.id] = p; });
    return map;
  }, [prefectures]);

  return (
    <div className="relative w-full">
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 opacity-15 blur-[80px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00ff41 0%, #00d9ff 40%, transparent 70%)' }}
      />

      <svg
        viewBox="0 0 420 440"
        className="w-full h-full relative z-10"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="japan-glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="japan-glow-strong">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <pattern id="japanGrid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ffffff" strokeWidth="0.2" strokeOpacity="0.06" />
          </pattern>
        </defs>

        <rect width="420" height="440" fill="url(#japanGrid)" />

        {Object.entries(PREF_POSITIONS).map(([id, pos]) => {
          const pref = prefMap[id];
          if (!pref) return null;
          const isHovered = hoveredPref === id;
          const isAvailable = pref.available;
          const regionColor = REGION_COLORS[pref.region] || '#333';

          return (
            <g
              key={id}
              className="cursor-pointer"
              onMouseEnter={() => {
                onHover(id);
                setTooltip({ x: pos.x + pos.w / 2, y: pos.y, pref });
              }}
              onMouseLeave={() => {
                onHover(null);
                setTooltip(null);
              }}
              onClick={() => onSelect(id)}
            >
              {isHovered && (
                <rect
                  x={pos.x - 3}
                  y={pos.y - 3}
                  width={pos.w + 6}
                  height={pos.h + 6}
                  rx="4"
                  fill="none"
                  stroke={regionColor}
                  strokeWidth="1.5"
                  opacity="0.6"
                  filter="url(#japan-glow-strong)"
                />
              )}

              <rect
                x={pos.x}
                y={pos.y}
                width={pos.w}
                height={pos.h}
                rx="3"
                fill={isHovered ? `${regionColor}30` : isAvailable ? `${regionColor}18` : 'rgba(255,255,255,0.03)'}
                stroke={isHovered ? regionColor : isAvailable ? `${regionColor}60` : 'rgba(255,255,255,0.08)'}
                strokeWidth={isHovered ? 1.5 : isAvailable ? 1 : 0.5}
                className="transition-all duration-200"
                filter={isHovered ? 'url(#japan-glow)' : undefined}
              />

              {isAvailable && (
                <circle
                  cx={pos.x + pos.w - 4}
                  cy={pos.y + 4}
                  r="2.5"
                  fill={regionColor}
                  filter="url(#japan-glow)"
                >
                  <animate
                    attributeName="opacity"
                    values="1;0.4;1"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </circle>
              )}

              <text
                x={pos.x + pos.w / 2}
                y={pos.y + pos.h / 2 + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={isHovered ? '#fff' : isAvailable ? regionColor : 'rgba(255,255,255,0.25)'}
                fontSize={pos.w > 35 ? '7.5' : '6'}
                fontFamily="Bebas Neue, sans-serif"
                letterSpacing="0.5"
                className="transition-all duration-200 pointer-events-none select-none"
              >
                {pref.nameEn.length > 8 ? pref.nameEn.slice(0, 7) + '..' : pref.nameEn}
              </text>
            </g>
          );
        })}

        {tooltip && (
          <g className="pointer-events-none">
            <rect
              x={Math.min(Math.max(tooltip.x - 50, 5), 315)}
              y={tooltip.y - 42}
              width="100"
              height="36"
              rx="6"
              fill="rgba(0,0,0,0.92)"
              stroke={REGION_COLORS[tooltip.pref.region] || '#333'}
              strokeWidth="1"
            />
            <text
              x={Math.min(Math.max(tooltip.x, 55), 365)}
              y={tooltip.y - 28}
              textAnchor="middle"
              fill="#fff"
              fontSize="9"
              fontFamily="Bebas Neue, sans-serif"
              letterSpacing="1"
            >
              {tooltip.pref.nameEn}
            </text>
            <text
              x={Math.min(Math.max(tooltip.x, 55), 365)}
              y={tooltip.y - 15}
              textAnchor="middle"
              fill="rgba(255,255,255,0.5)"
              fontSize="7"
              fontFamily="Noto Sans JP, sans-serif"
            >
              {tooltip.pref.name} · {tooltip.pref.artistCount} artists
            </text>
          </g>
        )}

        <text x="355" y="22" fill="#00d9ff" fontSize="6" fontFamily="Bebas Neue, sans-serif" letterSpacing="2" opacity="0.3">HOKKAIDO</text>
        <text x="348" y="130" fill="#00d9ff" fontSize="6" fontFamily="Bebas Neue, sans-serif" letterSpacing="2" opacity="0.3">TOHOKU</text>
        <text x="335" y="255" fill="#ff006e" fontSize="6" fontFamily="Bebas Neue, sans-serif" letterSpacing="2" opacity="0.3">KANTO</text>
        <text x="245" y="195" fill="#b537f2" fontSize="6" fontFamily="Bebas Neue, sans-serif" letterSpacing="2" opacity="0.3">CHUBU</text>
        <text x="160" y="240" fill="#00ff41" fontSize="6" fontFamily="Bebas Neue, sans-serif" letterSpacing="2" opacity="0.3">KINKI</text>
        <text x="85" y="255" fill="#ff8c00" fontSize="6" fontFamily="Bebas Neue, sans-serif" letterSpacing="2" opacity="0.3">CHUGOKU</text>
        <text x="25" y="268" fill="#ffd600" fontSize="6" fontFamily="Bebas Neue, sans-serif" letterSpacing="2" opacity="0.3">KYUSHU</text>
      </svg>
    </div>
  );
}
